//#region Imports
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { GenericMemberResponsePortalResponseDto } from '@app/core/models/genericMembeResponsePortalResponseDto';
import { Helper } from '@rawlings/core';
import { MatAccordion } from '@angular/material/expansion';
import { MemberResponseHelper } from '@app/member-response-portal/shared/member-response-helper';
import { NotificationRequestService } from '@app/member-response-portal/service/notification-request.service';
import { NotifierService } from '@app/core/services/notifier-service';
import { Observable, catchError, map, of } from 'rxjs';
import { NO_SYMBOLS_OR_SPACES_PATTERN, OTHER_HEALTH_INSURANCE_VALIDATIONS, POLICYHOLDERTYPE, SURVEYSTEPS_INDEX } from '@app/member-response-portal/model/constants';
import { OtherHealthInsuranceDto } from '@app/member-response-portal/model/otherHealthInsuranceDto';
import { OtherHealthInsuranceService } from '@app/member-response-portal/service/other-health-insurance.service';
import { PolicyHolderDto } from '@app/member-response-portal/model/policyHolderDto';
import { PolicyMemberDto } from '@app/member-response-portal/model/policyMemberDto';
import { SharedMemberResponseService } from '@app/member-response-portal/service/shared-member-response.service';
import { SurveyOtherHealthInsuranceHelper } from '@app/member-response-portal/shared/survey-other-health-insurance-helper';
import { SurveyStepBase } from '@app/member-response-portal/shared/survey-step-base';
//#endregion Imports
@Component({
  selector: 'app-survey-other-health-insurance',
  templateUrl: './survey-other-health-insurance.component.html',
  styleUrls: ['./survey-other-health-insurance.component.scss'],
})
export class SurveyOtherHealthInsuranceComponent extends SurveyStepBase implements OnInit {
  //#region Inputs, Outputs, ViewChilds
  @ViewChild(MatAccordion) public AccordionPolicyHolder!: MatAccordion;
  //#endregion Inputs, Outputs, ViewChilds

  //#region Public Variables
  public ShowHasOtherCommercialHealthInsurancePlan: boolean;
  public ShowQuestionPatientCoveredByMedicare: boolean;
  public ShowSectionPatientCoveredByMedicare: boolean;
  public InsuranceNameBeneficiaryMaxLength: number =
    OTHER_HEALTH_INSURANCE_VALIDATIONS.INSURANCE_NAME_BENEFICIARY_MAX_LENGTH;
  public InsuranceMedicareIdMaxLength: number =
    OTHER_HEALTH_INSURANCE_VALIDATIONS.INSURANCE_MEDICARE_ID_MAX_LENGTH;
  //#endregion Public Variables

  //#region Protected Variables
  //#endregion Protected Variables

  //#region Private Variables
  //#endregion Private Variables

  //#region Properties

  public get OtherHealthInsuranceId(): FormControl {
    return this.Form.get('OtherHealthInsuranceId') as FormControl;
  }

  public get HasOtherCommercialHealthInsurancePlan(): FormControl {
    return this.Form.get('HasOtherHealthInsurance') as FormControl;
  }

  public get HasOtherCoverage(): FormControl {
    return this.Form.get('HasCoverage') as FormControl;
  }

  public get PolicyHolders(): FormArray {
    return this.Form.get('PolicyHolders') as FormArray;
  }

  public get HasCoveredMedicare(): FormControl {
    return this.Form.get('HasCoveredMedicare') as FormControl;
  }

  public get InsuranceNameBeneficiary(): FormControl {
    return this.Form.get('InsuranceNameBeneficiary') as FormControl;
  }

  public get InsuranceMedicareId(): FormControl {
    return this.Form.get('InsuranceMedicareId') as FormControl;
  }

  public get InsuranceEffectiveDateHospital(): FormControl {
    return this.Form.get('InsuranceEffectiveDateHospital') as FormControl;
  }

  public get InsuranceEffectiveDateMedical(): FormControl {
    return this.Form.get('InsuranceEffectiveDateMedical') as FormControl;
  }

  public get ShowQuestionDoYouOtherCoverage(): boolean {
    return this.PolicyHolders.valid;
  }

  public get ShowToggleButtonExpandPolicyHolder(): boolean {
    return this.PolicyHolders.length > 2;
  }

  public get OtherHealthInsuranceDto(): OtherHealthInsuranceDto {
    const otherHealthInsuranceDto: OtherHealthInsuranceDto = {
      QueueLetterId: this.sharedMemberResponseService.GetQueueLetterId(),
      SurveyResponseId: this.SurveyResponseId,
      OtherHealthInsuranceId: this.OtherHealthInsuranceId.value,
      HasOtherHealthInsurance: MemberResponseHelper.GetBoolFromYesNoValue(
        this.HasOtherCommercialHealthInsurancePlan.value
      ),
      HasCoveredMedicare: MemberResponseHelper.GetBoolFromYesNoValue(
        this.HasCoveredMedicare.value
      ),
      InsuranceNameBeneficiary: MemberResponseHelper.GetValidString(
        this.InsuranceNameBeneficiary.value
      ),
      InsuranceMedicareId: this.InsuranceMedicareId.value,
      InsuranceEffectiveDateHospital: MemberResponseHelper.getDateWithZeroTime(
        this.InsuranceEffectiveDateHospital.value
      ),
      InsuranceEffectiveDateMedical: MemberResponseHelper.getDateWithZeroTime(
        this.InsuranceEffectiveDateMedical.value
      ),
      PolicyHolders: this.PolicyHoldersDtoArray,
      IsCompleted: this.Form.valid,
    };
    return otherHealthInsuranceDto;
  }

  private get PolicyHoldersDtoArray(): PolicyHolderDto[] {
    const policyHolderArrayDto: PolicyHolderDto[] = [];
    this.PolicyHolders.controls.forEach((policy: any) => {
      const additionalMembersDtoArray: PolicyMemberDto[] = [];
      const additionalMembersFormArray: FormArray = policy.get(
        'PolicyMembers'
      ) as FormArray;
      additionalMembersFormArray.controls.forEach((member: any) => {
        const policyMemberDto: PolicyMemberDto = {
          PolicyMemberId: member.get('PolicyMemberId')?.value,
          PolicyHolderId: member.get('PolicyHolderId')?.value,
          PolicyMemberName: member.get('PolicyMemberName')?.value,
          PolicyMemberDateOfBirth: MemberResponseHelper.getDateWithZeroTime(
            member.get('PolicyMemberDateOfBirth')?.value
          ),
          PolicyMemberIdentification: member.get('PolicyMemberIdentification')
            ?.value,
        };
        additionalMembersDtoArray.push(policyMemberDto);
      });
      const policyHolderDto: PolicyHolderDto = {
        PolicyHolderId: policy.get('PolicyHolderId')?.value,
        OtherHealthInsuranceId: this.OtherHealthInsuranceId.value,
        IsSelf: policy.get('IsSelf')?.value,
        IsSpouse: policy.get('IsSpouse')?.value,
        IsDependent: policy.get('IsDependent')?.value,
        IsOther: policy.get('IsOther')?.value,
        PolicyHolderOther:
          policy.get('IsOther')?.value === true
            ? MemberResponseHelper.GetValidString(
                policy.get('PolicyHolderOther')?.value
              )
            : null,
        PolicyHolderName: MemberResponseHelper.GetValidString(
          policy.get('PolicyHolderName')?.value
        ),
        PolicyHolderDateOfBirth: MemberResponseHelper.getDateWithZeroTime(
          policy.get('PolicyHolderDateOfBirth')?.value
        ),
        PolicyHolderIdentification: MemberResponseHelper.GetValidString(
          policy.get('PolicyHolderIdentification')?.value
        ),
        PolicyHolderNameOfTheCarrier: MemberResponseHelper.GetValidString(
          policy.get('PolicyHolderNameOfTheCarrier')?.value
        ),
        PolicyCarrierCustomerService: MemberResponseHelper.GetValidPhoneNumber(
          policy.get('PolicyCarrierCustomerService')?.value
        ),
        PolicyMembers: additionalMembersDtoArray,
        PolicyHolderType: policy.get('PolicyHolderType')?.value,
        HasOtherPolicyMembers: policy.get('HasOtherPolicyMembers')?.value,
      };
      policyHolderArrayDto.push(policyHolderDto);
    });

    return policyHolderArrayDto;
  }
  //#endregion Properties

  //#region Constructor
  constructor(
    protected changeDetectorRef: ChangeDetectorRef,
    private notificationRequestService: NotificationRequestService,
    protected notifierService: NotifierService,
    private otherHealthInsuranceService: OtherHealthInsuranceService,
    protected sharedMemberResponseService: SharedMemberResponseService
  ) {
    super(
      `Other Health Insurance`,
      SURVEYSTEPS_INDEX.OTHER_HEALTH_INSURANCE,
      true,
      'OtherHealthInsurance',
      changeDetectorRef,
      notifierService,
      sharedMemberResponseService
    );
  }

  public ngOnInit(): void {
    this.InitStep();
  }
  //#endregion Constructor

  //#region Public Methods
  public SaveForm(): Observable<boolean> {
    return this.otherHealthInsuranceService
      .Save(this.OtherHealthInsuranceDto)
      .pipe(
        map(
          (result: GenericMemberResponsePortalResponseDto<number>): boolean => {
            if (result.Success) {
              this.OtherHealthInsuranceId.setValue(result.Data);
              this.sharedMemberResponseService.UpdateSurveySummary(this.FormKey, this.OtherHealthInsuranceDto);
              return true;
            } else {
                result.IsSurveySubmitted ? this.notificationRequestService.ShowFailedRequestAndLogout(result) : this.notificationRequestService.ShowFailedRequestAndLogout(null);
              return false;
            }
          }
        ),
        catchError(() => {
          return of(false);
        })
      );
  }

  public AddPolicyHolder(): void {
    this.PolicyHolders.push(
      SurveyOtherHealthInsuranceHelper.CreatePolicyHolderFormGroup()
    );
  }

  public ExpandAllPolicyHolders(): void {
    this.AccordionPolicyHolder.openAll();
  }

  public CollapseAllPolicyHolders(): void {
    this.AccordionPolicyHolder.closeAll();
  }

  public HandleHasOtherCommercialHealthInsurancePlan(event: {
    IsChecked: boolean;
    IsConfirmed: boolean;
  }): void {
    if (
      (!Helper.IsBoolean(event.IsChecked) || Helper.IsEmpty(event.IsChecked)) &&
      event.IsConfirmed
    ) {
      this.ResetPolicyHolderSectionForm();
      this.VisibleSection_HasOtherCommercialHealthInsurancePlan(false);
    } else {
      this.HasOtherCommercialHealthInsurancePlan.setValue('Yes');
      this.VisibleSection_HasOtherCommercialHealthInsurancePlan(true);
      if (!this.PolicyHolders.length) {
        this.AddPolicyHolder();
      }
      this.HasOtherCommercialHealthInsurancePlan.updateValueAndValidity();
    }
  }

  public HandleHasCoveredMedicare(event: {
    IsChecked: boolean;
    IsConfirmed: boolean;
  }): void {
    if (
      (!Helper.IsBoolean(event.IsChecked) || Helper.IsEmpty(event.IsChecked)) &&
      event.IsConfirmed
    ) {
      this.ResetMedicareCoveredSectionForm();
      this.VisibleSection_HasCoveredMedicare(false);
    } else {
      this.HasCoveredMedicare.setValue('Yes');
      this.VisibleSection_HasCoveredMedicare(true);
      this.SetHasCoveredMedicareFieldsValidation();
    }
  }

  public HandleHasOtherCoverage(event: {
    IsChecked: boolean;
    IsConfirmed: boolean;
  }): void {
    if (
      (!Helper.IsBoolean(event.IsChecked) || Helper.IsEmpty(event.IsChecked)) &&
      event.IsConfirmed
    ) {
      this.VisibleSection_QuestionPatientCoveredByMedicare(true);
    } else {
      this.AddPolicyHolder();
      this.HasOtherCoverage.setValue(null);
      this.HasOtherCoverage.updateValueAndValidity();
    }
  }

  public SkipValueHasOtherHealthInsurance(): boolean {
    return this.PolicyHolders?.length > 0 &&
      this.IsDirtyPolicyHolderSectionForm()
      ? false
      : true;
  }

  public SkipValueCoveredMedicare(): boolean {
    return this.IsDirtyMedicareCoveredSectionForm() ? false : true;
  }

  public SetErrorTooltipMessage(value: string, message: string) : string {
    if (this.Form.get(value).hasError('pattern')) {
        return `Invalid ${message}`;
    }

    return null;
}
  //#endregion Public Methods

  //#region Protected Methods
  protected BuildForm() : void{
    this.Form =
    SurveyOtherHealthInsuranceHelper.CreateOtherHealthInsuranceFormGroup(
      this.SurveyResponseId
    );
  }

  protected GetFormData() : any {
    return this.OtherHealthInsuranceDto;
  }

  protected HandleRequest(): Observable<boolean> {
    return this.otherHealthInsuranceService
      .Save(this.OtherHealthInsuranceDto)
      .pipe(
        map(
          (
            result: GenericMemberResponsePortalResponseDto<number>
          ): boolean => {
            if (result.Success) {
              this.OtherHealthInsuranceId.setValue(result.Data);
              return true;
            } else {
              this.notificationRequestService.ShowFailedRequestAndLogout(
                result
              );
              return false;
            }
          }
        ),
        catchError(() => {
          return of(false);
        })
      );
  }

  protected LoadData(): void{
    const ininitialData: any = this.sharedMemberResponseService.GetSurveySummarySection(this.FormKey);
    if(ininitialData){
        this.SetInitialForm(ininitialData);
        this.SetDataToForm(ininitialData);
        this.Form.updateValueAndValidity();
    }
  }
  //#endregion Protected Methods
  //#region Private Methods
  private IsDirtyPolicyHolderSectionForm(): boolean {
    return this.PolicyHolders.dirty;
  }

  private SetInitialForm(
    otherHealthInsuranceDto: OtherHealthInsuranceDto
  ): void {
    if (otherHealthInsuranceDto.HasOtherHealthInsurance) {
      if (
        otherHealthInsuranceDto.PolicyHolders !== null &&
        otherHealthInsuranceDto.PolicyHolders.length
      ) {
        this.UpdatePolicyHoldersFormArrayByDto(
          otherHealthInsuranceDto.PolicyHolders
        );
      } else {
        this.PolicyHolders.push(
          SurveyOtherHealthInsuranceHelper.CreatePolicyHolderFormGroup()
        );
      }
    }
  }

  private UpdatePolicyHoldersFormArrayByDto(
    policyHoldersArrayDto: PolicyHolderDto[]
  ): void {
    this.PolicyHolders.clear();
    policyHoldersArrayDto.forEach((policy: PolicyHolderDto) => {
      const policyHolderFormGroup: FormGroup =
        SurveyOtherHealthInsuranceHelper.CreatePolicyHolderFormGroup();
      if (policy.PolicyMembers !== null && policy.PolicyMembers.length) {
        const additionalMembersFormArray: FormArray = policyHolderFormGroup.get(
          'PolicyMembers'
        ) as FormArray;
        policy.PolicyMembers.map(() =>
          additionalMembersFormArray.push(
            SurveyOtherHealthInsuranceHelper.CreatePolicyHolderMemberFormGroup()
          )
        );
        additionalMembersFormArray.markAsDirty();
      }
      this.PolicyHolders.push(policyHolderFormGroup);
    });
    this.PolicyHolders.markAsDirty();
  }

  private SetDataToForm(
    otherHealthInsuranceDto: OtherHealthInsuranceDto
  ): void {
    this.SetFormValues(otherHealthInsuranceDto);
    this.VisibleSection_HasOtherCommercialHealthInsurancePlan(
      otherHealthInsuranceDto.HasOtherHealthInsurance
    );
    this.VisibleSection_QuestionPatientCoveredByMedicare(true);
    this.VisibleSection_HasCoveredMedicare(
      otherHealthInsuranceDto.HasCoveredMedicare
    );
    this.Form.updateValueAndValidity();
  }

  private SetFormValues(
    otherHealthInsuranceDto: OtherHealthInsuranceDto
  ): void {
    this.Form.patchValue({
      SurveyResponseId: otherHealthInsuranceDto.SurveyResponseId,
      OtherHealthInsuranceId: otherHealthInsuranceDto.OtherHealthInsuranceId,
      HasOtherHealthInsurance: MemberResponseHelper.GetStringToggleValue(
        otherHealthInsuranceDto.HasOtherHealthInsurance
      ),
      HasCoverage: MemberResponseHelper.GetStringForYesNoValue(false),
      HasCoveredMedicare: MemberResponseHelper.GetStringToggleValue(
        otherHealthInsuranceDto.HasCoveredMedicare
      ),
      InsuranceNameBeneficiary:
        otherHealthInsuranceDto.InsuranceNameBeneficiary,
      InsuranceMedicareId: otherHealthInsuranceDto.InsuranceMedicareId,
      InsuranceEffectiveDateHospital:
          otherHealthInsuranceDto.InsuranceEffectiveDateHospital,
      InsuranceEffectiveDateMedical:
          otherHealthInsuranceDto.InsuranceEffectiveDateMedical,
      PolicyHolders: this.MapPolicyHolders(
        otherHealthInsuranceDto.PolicyHolders
      ),
    });
  }

  private MapPolicyHolders(
    policyHolders: PolicyHolderDto[]
  ): PolicyHolderDto[] {
    return policyHolders === null
      ? []
      : policyHolders.map((policy: PolicyHolderDto) => {
          policy.PolicyHolderType = this.SetPolicyHolderTypeValue(policy);
          policy.HasOtherPolicyMembers =
            MemberResponseHelper.GetStringForYesNoValue(
              policy.PolicyMembers.length > 0
            );
          return policy;
        });
  }

  private SetPolicyHolderTypeValue(policy: PolicyHolderDto): string {
    return policy.IsSelf
      ? POLICYHOLDERTYPE.IsSelf
      : policy.IsSpouse
      ? POLICYHOLDERTYPE.IsSpouse
      : policy.IsDependent
      ? POLICYHOLDERTYPE.IsDependent
      : policy.IsOther
      ? POLICYHOLDERTYPE.IsOther
      : '';
  }

  private ResetPolicyHolderSectionForm(): void {
    this.PolicyHolders.clear();
    this.HasOtherCoverage.reset();
  }

  private ResetMedicareCoveredSectionForm(): void {
    this.InsuranceNameBeneficiary.clearValidators();
    this.InsuranceNameBeneficiary.reset(null);

    this.InsuranceMedicareId.clearValidators();
    this.InsuranceMedicareId.reset(null);

    this.InsuranceEffectiveDateHospital.clearValidators();
    this.InsuranceEffectiveDateHospital.reset('');

    this.InsuranceEffectiveDateMedical.clearValidators();
    this.InsuranceEffectiveDateMedical.reset('');

    this.Form.updateValueAndValidity();
  }

  private SetHasCoveredMedicareFieldsValidation(): void {
    this.InsuranceNameBeneficiary.setValidators([Validators.required, Validators.pattern(NO_SYMBOLS_OR_SPACES_PATTERN)]);
    this.InsuranceNameBeneficiary.updateValueAndValidity();

    this.InsuranceMedicareId.setValidators([Validators.required, Validators.pattern(NO_SYMBOLS_OR_SPACES_PATTERN)]);
    this.InsuranceMedicareId.updateValueAndValidity();

    this.InsuranceEffectiveDateHospital.setValidators([Validators.required]);
    this.InsuranceEffectiveDateHospital.updateValueAndValidity();

    this.InsuranceEffectiveDateMedical.setValidators([Validators.required]);
    this.InsuranceEffectiveDateMedical.updateValueAndValidity();
  }

  private IsDirtyMedicareCoveredSectionForm(): boolean {
    return (
      this.InsuranceNameBeneficiary.value ||
      this.InsuranceMedicareId.value ||
      this.InsuranceEffectiveDateHospital.value ||
      this.InsuranceEffectiveDateMedical.value
    );
  }

  private VisibleSection_HasOtherCommercialHealthInsurancePlan(
    value: boolean
  ): void {
    this.ShowHasOtherCommercialHealthInsurancePlan = value;
    this.SetHasOtherCoverageFieldValidation(value);
  }

  private VisibleSection_HasCoveredMedicare(value: boolean): void {
    this.ShowSectionPatientCoveredByMedicare = MemberResponseHelper.GetBooleanToggleValue(value);
    if (this.ShowSectionPatientCoveredByMedicare ) {
      this.SetHasCoveredMedicareFieldsValidation();
    } else {
      this.ResetMedicareCoveredSectionForm();
    }
  }

  private VisibleSection_QuestionPatientCoveredByMedicare(
    value: boolean
  ): void {
    this.ShowQuestionPatientCoveredByMedicare = value;
  }

  private SetHasOtherCoverageFieldValidation(isRequired: boolean): void {
    if (isRequired) {
      this.HasOtherCoverage.setValidators([Validators.required]);
    } else {
      this.HasOtherCoverage.clearValidators();
    }
    this.HasOtherCoverage.updateValueAndValidity();
  }
  //#region Private Methods
}
